import { useRef, useState } from "react";
import { Upload, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { fileToResizedDataUrl, fileToDataUrl } from "../lib/imageUtils";
import { api } from "../lib/api";

export function UploadZone({ onImages, compact = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const collectedUrls = [];
      const collectedNames = [];
      for (const file of Array.from(files)) {
        if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          const dataUrl = await fileToDataUrl(file);
          const { images } = await api.convertPdf(dataUrl);
          images.forEach((img, i) => {
            collectedUrls.push(img);
            collectedNames.push(images.length > 1 ? `${file.name} — p.${i + 1}` : file.name);
          });
        } else if (file.type.startsWith("image/")) {
          collectedUrls.push(await fileToResizedDataUrl(file));
          collectedNames.push(file.name);
        } else {
          toast.error(`Niet-ondersteund bestand: ${file.name}`);
        }
      }
      if (collectedUrls.length) {
        onImages(collectedUrls, collectedNames);
        toast.success(`${collectedUrls.length} pagina('s) toegevoegd`);
      }
    } catch (e) {
      toast.error("Kon bestand niet verwerken");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="upload-zone"
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={`group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all ${
        drag ? "border-[var(--brand)] bg-[var(--surface-muted)]" : "border-[var(--border-active)] hover:border-[var(--brand)]"
      } ${compact ? "p-5" : "p-10 sm:p-16"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        data-testid="upload-letter-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--brand)] transition-transform group-hover:scale-110">
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
      </div>
      <p className="mt-4 font-display text-base font-semibold text-[var(--ink)]">
        {busy ? "Bezig met verwerken…" : "Sleep een scan hierheen of klik om te uploaden"}
      </p>
      {!compact && (
        <p className="mt-1.5 flex items-center gap-3 text-sm text-[var(--ink-3)]">
          <span className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> JPG · PNG</span>
          <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> PDF</span>
        </p>
      )}
    </div>
  );
}
